import { Car, ExternalLink } from 'lucide-react'
import PropTypes from 'prop-types'
import { FormModal } from '@components/branded-modal'
import { describeVehicleInProgress } from '@pages/adas-intelligence/adapters'
import { getInvoiceSummaryUrl, getQuoteSummaryUrl, getWorkOrderSummaryUrl } from '../../../constants'

const CARD_BUTTON_CLASS =
  'inline-flex items-center gap-1.5 rounded-md border border-[#D1D5D8] bg-white px-3 py-1.5 text-[12px] font-medium text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
const FOOTER_BUTTON_CLASS =
  'inline-flex items-center justify-center rounded-lg px-5 py-2.5 text-[13px] font-semibold uppercase transition-colors'

const STAGES = {
  QUOTE: { label: 'Quote', url: getQuoteSummaryUrl },
  WORK_ORDER: { label: 'Work Order', url: getWorkOrderSummaryUrl },
  INVOICE: { label: 'Invoice', url: getInvoiceSummaryUrl }
}

const KINDS = {
  quote: { eyebrow: 'New Quote', noun: 'quote' },
  workOrder: { eyebrow: 'New Work Order', noun: 'work order' }
}

/**
 * CAR-4021 — shown when CREATE QUOTE / CREATE WORK ORDER is refused because the same job (body shop, RO,
 * VIN) is still open: names that record, with View opening it in a new tab so the form stays filled.
 * There is no override; CLOSE returns to the form.
 */
export const VehicleInProgressDialog = ({ kind, refusal, onClose }) => {
  if (!refusal) return null
  const { existing } = refusal
  const copy = KINDS[kind] || KINDS.quote
  const stage = STAGES[existing?.stage] || STAGES.QUOTE
  const ro = existing?.repair_order_num?.trim()

  const footer = (
    <div className='flex flex-wrap justify-end gap-3'>
      <button type='button' autoFocus onClick={onClose} className={`${FOOTER_BUTTON_CLASS} bg-[#0A1628] text-white hover:bg-[#1F2D3D]`}>
        Close
      </button>
    </div>
  )

  return (
    <FormModal open hideClose onClose={onClose} icon={Car} eyebrow={copy.eyebrow} title='Vehicle Already In Progress' footer={footer}>
      <p className='text-[13px] leading-5 text-[#334155]'>
        {describeVehicleInProgress(existing) || refusal.message} A new {copy.noun} for this body shop, RO and VIN can&apos;t be created
        while it is open.
      </p>

      {existing?.reference_number && (
        <div data-testid='vehicle-in-progress-record' className='mt-4 rounded-xl border border-[#EBEDF0] bg-[#FAFBFC] px-4 py-3'>
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <div>
              <div className='text-[14px] font-semibold text-[#0A1628]'>
                {stage.label} #{existing.reference_number}
              </div>
              {ro && <div className='font-mono text-[12px] text-[#475569]'>RO {ro}</div>}
            </div>
            <a href={stage.url(existing.reference_number)} target='_blank' rel='noopener noreferrer' className={CARD_BUTTON_CLASS}>
              <ExternalLink className='h-3.5 w-3.5' />
              View {stage.label}
            </a>
          </div>
        </div>
      )}

      <p className='mt-4 text-[12px] text-[#475569]'>
        Continue on the open {stage.label.toLowerCase()}, or check the RO number if this is a different job.
      </p>
    </FormModal>
  )
}

VehicleInProgressDialog.propTypes = {
  kind: PropTypes.oneOf(['quote', 'workOrder']).isRequired,
  refusal: PropTypes.shape({
    message: PropTypes.string,
    existing: PropTypes.shape({
      reference_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      stage: PropTypes.string,
      repair_order_num: PropTypes.string
    })
  }),
  onClose: PropTypes.func.isRequired
}
